import React, { useRef, useEffect } from 'react';
import { useDiary, getViewportHeight, pixelY, BASE_PX_PER_MINUTE } from './DiaryContext';
import TimeAxis from './TimeAxis';
import StaffColumn from './StaffColumn';
import NowIndicator from './NowIndicator';
import WeekView from './WeekView';
import MonthView from './MonthView';

/**
 * DiaryViewport - The single scrollable container for the entire diary grid.
 * Contains the time axis (sticky left), staff columns, and the now indicator.
 */
function DiaryViewport() {
  const { state } = useDiary();
  const { staffOrder, staffById, eventsByStaff, viewStartMinutes, viewEndMinutes, zoomLevel, initialLoadComplete, filterMode, filteredStaffIds, viewMode } = state;

  // Apply filter
  const visibleStaffOrder = React.useMemo(() => {
    if (filterMode === 'all') return staffOrder;
    if (filterMode === 'working') return staffOrder.filter(id => staffById[id]?.isWorking);
    return staffOrder.filter(id => filteredStaffIds.includes(id));
  }, [staffOrder, staffById, filterMode, filteredStaffIds]);
  const scrollRef = useRef(null);
  const hasScrolledRef = useRef(false);
  const prevZoomRef = useRef(zoomLevel);

  const totalHeight = getViewportHeight(viewStartMinutes, viewEndMinutes, zoomLevel);

  // Keep the viewport centred on the same time when zoom changes
  useEffect(() => {
    const prev = prevZoomRef.current;
    prevZoomRef.current = zoomLevel;
    if (prev === zoomLevel || !scrollRef.current) return;

    const el = scrollRef.current;
    const centreY = el.scrollTop + el.clientHeight / 2;
    const centreMinutes = viewStartMinutes + centreY / (BASE_PX_PER_MINUTE * prev);
    const newCentreY = (centreMinutes - viewStartMinutes) * BASE_PX_PER_MINUTE * zoomLevel;
    el.scrollTop = newCentreY - el.clientHeight / 2;
  }, [zoomLevel, viewStartMinutes]);

  // Auto-scroll to first working hour on initial load
  useEffect(() => {
    if (!initialLoadComplete || hasScrolledRef.current || !scrollRef.current) return;
    hasScrolledRef.current = true;

    // Find earliest working start
    let earliestStart = null;
    staffOrder.forEach(id => {
      const s = staffById[id];
      if (s?.isWorking && s.startMinutes != null) {
        if (earliestStart === null || s.startMinutes < earliestStart) {
          earliestStart = s.startMinutes;
        }
      }
    });

    if (earliestStart !== null) {
      // Scroll to 30 min before earliest start so there's a small buffer
      const scrollTarget = Math.max(0, earliestStart - 30);
      const scrollPx = pixelY(scrollTarget, viewStartMinutes, zoomLevel);
      scrollRef.current.scrollTop = scrollPx;
    }
  }, [initialLoadComplete, staffOrder, staffById, viewStartMinutes, zoomLevel]);

  if (!initialLoadComplete) {
    return <DiarySkeleton />;
  }

  if (viewMode === 'week') {
    return <WeekView />;
  }

  if (viewMode === 'month') {
    return <MonthView />;
  }

  if (visibleStaffOrder.length === 0) {
    return (
      <div className="dv2-viewport dv2-viewport--empty">
        <div className="dv2-empty-text">No staff to display</div>
      </div>
    );
  }

  return (
    <div className="dv2-viewport" ref={scrollRef}>
      <div className="dv2-viewport-inner">
        <TimeAxis />
        <div className="dv2-columns-container" style={{ minHeight: totalHeight }}>
          <NowIndicator />
          {visibleStaffOrder.map(staffId => {
            const staff = staffById[staffId];
            // An order can land a moment before its staff data (the order is
            // dispatched first); a column with nothing to draw waits a frame.
            if (!staff) return null;
            const events = eventsByStaff[staffId] || [];
            return (
              <StaffColumn
                key={staffId}
                staff={staff}
                events={events}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * DiarySkeleton - Floating booking blocks that rise up while data loads.
 * Adapts V1's DiarySkeleton pattern for V2's layout.
 */
var SKEL_FLOATERS = [
  [
    { height: 64, delay: 0, duration: 3.2, opacity: 0.45 },
    { height: 40, delay: 1.1, duration: 2.8, opacity: 0.3 },
    { height: 52, delay: 2.4, duration: 3.5, opacity: 0.38 },
  ],
  [
    { height: 48, delay: 0.4, duration: 3.0, opacity: 0.35 },
    { height: 72, delay: 1.8, duration: 3.4, opacity: 0.42 },
    { height: 36, delay: 3.0, duration: 2.6, opacity: 0.28 },
  ],
  [
    { height: 56, delay: 0.8, duration: 3.6, opacity: 0.4 },
    { height: 44, delay: 2.0, duration: 2.9, opacity: 0.32 },
    { height: 68, delay: 0.2, duration: 3.1, opacity: 0.44 },
  ],
  [
    { height: 42, delay: 1.4, duration: 3.3, opacity: 0.36 },
    { height: 60, delay: 0.6, duration: 2.7, opacity: 0.4 },
    { height: 50, delay: 2.6, duration: 3.0, opacity: 0.3 },
  ],
];

function DiarySkeleton() {
  var isMobile = window.innerWidth < 768;
  var colCount = isMobile ? 2 : 4;

  return (
    <div className="dv2-viewport dv2-viewport--skeleton">
      {/* Fake time axis */}
      <div className="dv2-skel-time-axis" />
      {/* Floating columns */}
      <div className="dv2-skel-columns">
        {Array.from({ length: colCount }).map(function (_, ci) {
          var floaters = SKEL_FLOATERS[ci % SKEL_FLOATERS.length];
          return (
            <div className="dv2-skel-col" key={ci}>
              {/* Skeleton header */}
              <div className="dv2-skel-header">
                <div className="dv2-skel-avatar" />
                <div className="dv2-skel-name" />
                <div className="dv2-skel-hours" />
              </div>
              {/* Floating blocks */}
              <div className="dv2-skel-body">
                {floaters.map(function (f, fi) {
                  return (
                    <div
                      className="dv2-skel-floater"
                      key={fi}
                      style={{
                        height: f.height,
                        opacity: f.opacity,
                        animationDelay: f.delay + 's',
                        animationDuration: f.duration + 's',
                      }}
                    />
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default React.memo(DiaryViewport);
